'use client';
import { useRouter } from 'next/navigation';
import { getRedirectUrlFromCookies, loginWithPassword, logout } from './auth';
import { REDIRECT_URL } from './constants';
import { useAuthContext } from './context';
import { getDefaultRedirectAfterLogin, getDefaultRedirectAfterLogout } from './setup';

export const useUser = () => {
  const { user, isLoading } = useAuthContext();
  return { user, isLoading };
};

/**
 * @description This hook is used to logout the user. It removes the access and refresh tokens from the cookies and sets the tokens in the context to null.
 *
 * @returns {Function} loginWithPassword function
 * @example
 * ```ts
 * const loginWithPassword = useLoginWithPassword();
 * loginWithPassword(email, password);
 * ```
 *
 * @todo Potentially make the redirect URL configurable and optional
 *
 */
export const useLoginWithPassword = () => {
  const { setUser } = useAuthContext();
  const router = useRouter();
  return async (email: string, password: string) => {
    await loginWithPassword(email, password, setUser);
    const redirectUrl = getRedirectUrlFromCookies(REDIRECT_URL) ?? getDefaultRedirectAfterLogin();
    router.push(redirectUrl);
  };
};

/**
 * @description This hook is used to logout the user. It removes the access and refresh tokens from the cookies and sets the tokens in the context to null.
 *
 * @returns {Function} logout function
 * @example
 * ```ts
 * const logout = useLogout();
 * logout();
 * ```
 *
 * @todo Potentially make the redirect URL configurable and optional
 *
 */
export const useLogout = () => {
  const { setUser } = useAuthContext();
  const router = useRouter();
  return () => {
    logout(setUser);
    router.push(getDefaultRedirectAfterLogout());
  };
};
